import React from 'react';

interface ClockNumberProps {
  number: number;
  angle: number;
  radius: number;
  isActive: boolean;
  hasEvents: boolean;
  onClick: () => void;
}

const ClockNumber: React.FC<ClockNumberProps> = ({
  number,
  angle,
  radius,
  isActive,
  hasEvents,
  onClick
}) => {
  // Calculate position
  const x = Math.sin((angle * Math.PI) / 180) * radius;
  const y = -Math.cos((angle * Math.PI) / 180) * radius;

  // Helper function to get CSS variable with fallback
  const getColorVar = (varName: string, fallback: string) => {
    return `var(${varName}, ${fallback})`;
  };

  // Color variables
  const activeColor = getColorVar('--color-primary', '#dc2626');
  const activeColorLight = getColorVar('--color-primary-light', '#ef4444');
  const hoverColor = getColorVar('--color-secondary', '#3b82f6');
  const accentColor = getColorVar('--color-accent', '#8b5cf6');
  const hasEventsColor = getColorVar('--color-text-muted', '#a8a29e');
  const inactiveColor = getColorVar('--color-text-secondary', '#71717a');
  const dotColor = getColorVar('--color-warning', '#f97316');

  // Get RGB values for shadow
  const getRGBFromVar = (varName: string, fallbackRGB: string) => {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    return value || fallbackRGB;
  };

  const primaryRGB = getRGBFromVar('--color-primary-rgb', '220, 38, 38');

  return (
    <button
      onClick={onClick}
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
        width: '48px',
        height: '48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Orbitron, sans-serif',
        fontSize: '18px',
        fontWeight: isActive ? 700 : 500,
        color: isActive ? activeColor : hasEvents ? hasEventsColor : inactiveColor,
        background: isActive 
          ? `radial-gradient(circle, rgba(${primaryRGB}, 0.2) 0%, transparent 70%)`
          : 'transparent',
        border: 'none',
        borderRadius: '50%',
        cursor: hasEvents ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        textShadow: isActive 
          ? `0 0 20px ${activeColorLight}, 0 0 40px rgba(${primaryRGB}, 0.5)`
          : hasEvents 
            ? `0 0 10px rgba(${primaryRGB}, 0.3)`
            : 'none',
        zIndex: 20,
      }}
      onMouseEnter={(e) => {
        if (hasEvents) {
          e.currentTarget.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(1.2)`;
          e.currentTarget.style.color = hoverColor;
          e.currentTarget.style.textShadow = `0 0 25px ${activeColorLight}, 0 0 50px rgba(${primaryRGB}, 0.5)`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(1)`;
        e.currentTarget.style.color = isActive ? activeColor : hasEvents ? hasEventsColor : inactiveColor;
        e.currentTarget.style.textShadow = isActive 
          ? `0 0 20px ${activeColorLight}, 0 0 40px rgba(${primaryRGB}, 0.5)`
          : hasEvents 
            ? `0 0 10px rgba(${primaryRGB}, 0.3)`
            : 'none';
      }}
    >
      {number}
      {hasEvents && (
        <span
          style={{
            position: 'absolute',
            bottom: '-2px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '6px',
            height: '6px',
            backgroundColor: isActive ? activeColor : dotColor,
            borderRadius: '50%',
            boxShadow: isActive ? `0 0 8px ${activeColorLight}` : 'none',
          }}
        />
      )}
    </button>
  );
};

export default ClockNumber;